/**
 * Error codes, the RFC 9457 problem shape, and `DomainError`.
 *
 * Modules throw `new DomainError('ORDER_ALREADY_SETTLED', { detail })`; the
 * HTTP layer calls `toProblem(err)` and sends the result as
 * `application/problem+json` with `problem.status`. Codes are stable strings:
 * clients branch on `code`, never on `title` or `detail`.
 */
import { z } from 'zod';
import { PERMISSION_KEY_PATTERN, type PermissionKey } from './permissions.js';

interface ErrorCodeSpec {
  readonly status: number;
  readonly title: string;
}

/** The catalogue: code → HTTP status and a fixed human title. */
export const ERROR_CODES = {
  VALIDATION_FAILED: { status: 400, title: 'The request is not valid' },
  UNAUTHENTICATED: { status: 401, title: 'Authentication is required' },
  FORBIDDEN: { status: 403, title: 'You do not have permission to do this' },
  NOT_FOUND: { status: 404, title: 'Not found' },
  VERSION_CONFLICT: { status: 409, title: 'The resource was changed by someone else' },
  IDEMPOTENCY_KEY_REQUIRED: { status: 400, title: 'An Idempotency-Key header is required' },
  IDEMPOTENCY_MISMATCH: {
    status: 422,
    title: 'The Idempotency-Key was already used with a different request',
  },
  IN_PROGRESS: { status: 409, title: 'A request with this Idempotency-Key is still in progress' },
  OVERRIDE_REQUIRED: { status: 403, title: 'A manager override is required' },
  RATE_LIMITED: { status: 429, title: 'Too many requests' },
  // Sign-in (iam). One code for every wrong secret (password, OTP, PIN, TOTP code) so
  // a response never tells which part was wrong or whether the account exists.
  INVALID_CREDENTIALS: { status: 401, title: 'The sign-in details are not correct' },
  TOTP_REQUIRED: { status: 401, title: 'Enter the code from your authenticator app' },
  ACCOUNT_LOCKED: { status: 423, title: 'Too many failed attempts; try again later' },
  COMPANY_SELECTION_REQUIRED: { status: 409, title: 'Choose which company to sign in to' },
  TERMINAL_BLOCKED: { status: 403, title: 'This terminal is blocked' },
  TERMINAL_NOT_REGISTERED: {
    status: 403,
    title: 'This device is not a registered terminal of the outlet',
  },
  TENANT_CONTEXT_MISSING: { status: 500, title: 'Tenant context is missing' },
  // Realtime catch-up (GET {o}/changes): the cursor is older than the 24 h window or
  // unknown to this server; refetch the full state, then follow from a new cursor.
  CURSOR_EXPIRED: { status: 410, title: 'The change cursor has expired; refetch the full state' },
  ORDER_ALREADY_SETTLED: { status: 409, title: 'The order is already settled' },
  // Orders (M3 task 1, packages/domain/src/orders): the order and line state machines
  // and the split/merge/move/transfer rules refuse with these codes.
  ORDER_CANCELLED: { status: 409, title: 'The order is cancelled or rejected' },
  ORDER_BILLED: { status: 409, title: 'The order is billed; reopen it to change it' },
  ORDER_STATUS_INVALID: { status: 409, title: 'The order cannot do this in its current status' },
  ORDER_HAS_PAYMENTS: { status: 409, title: 'The order has payments recorded' },
  ORDER_NOT_FULLY_PAID: { status: 409, title: 'The order is not fully paid' },
  ORDER_EMPTY: { status: 409, title: 'The order has no items' },
  ORDER_NOT_PRICED: { status: 409, title: 'The order total is out of date; re-price it first' },
  PAYMENT_EXCEEDS_DUE: { status: 409, title: 'The payment is more than the amount due' },
  PAYMENT_VOIDED: { status: 409, title: 'The payment is voided' },
  PAYMENT_NOT_CORRECTABLE: { status: 409, title: 'This payment cannot be corrected' },
  PAYMENT_METHOD_UNAVAILABLE: {
    status: 409,
    title: 'This payment method is not offered at the outlet',
  },
  PAYMENT_INTENT_CLOSED: { status: 409, title: 'The payment request is no longer open' },
  FULFILMENT_STATUS_INVALID: { status: 409, title: 'The fulfilment status cannot change this way' },
  LINE_ALREADY_SENT: { status: 409, title: 'The item was already sent to the kitchen' },
  LINE_ALREADY_VOIDED: { status: 409, title: 'The item is already voided' },
  LINE_STATUS_INVALID: { status: 409, title: 'The item cannot do this in its current status' },
  KOT_PENDING: { status: 409, title: 'A kitchen ticket for this item is still printing' },
  // Kitchen (M3 task 3): nothing to print on, and a device confirming another's job.
  PRINTER_NOT_CONFIGURED: { status: 409, title: 'No printer is set up for this' },
  INVOICE_SERIES_IN_USE: {
    status: 409,
    title: 'Invoices were issued in this series; its numbering cannot change',
  },
  INVOICE_SERIES_FULL: { status: 409, title: 'The invoice series has no numbers left this year' },
  // Offline numbering (M4 task 5): a bill numbered from a block the terminal does not hold.
  INVOICE_BLOCK_INVALID: {
    status: 409,
    title: 'The invoice number is not in a block reserved for this terminal',
  },
  INVOICE_BLOCK_CLOSED: {
    status: 409,
    title: 'The invoice block was closed; its numbers are void',
  },
  INVOICE_NUMBER_USED: { status: 409, title: 'The invoice number was already used' },
  INVOICE_BLOCK_FY_MISMATCH: {
    status: 409,
    title: 'The bill belongs to another financial year than its invoice block',
  },
  KOT_NUMBER_INVALID: {
    status: 409,
    title: 'The KOT number is not in a range reserved for this terminal',
  },
  DAY_ALREADY_OPEN: { status: 409, title: 'The business day is already open' },
  // Accounting part 2 (M5 task 8): shifts, cash and day close.
  DAY_ALREADY_CLOSED: { status: 409, title: 'The business day is already closed' },
  DAY_CLOSE_BLOCKED: { status: 409, title: 'The business day cannot be closed yet' },
  SHIFT_ALREADY_OPEN: { status: 409, title: 'A shift is already open here' },
  SHIFT_NOT_OPEN: { status: 409, title: 'The shift is closed' },
  INSUFFICIENT_CASH: { status: 409, title: 'The cash account does not hold that much' },
  PRINT_JOB_NOT_YOURS: { status: 403, title: 'This print job is for another terminal' },
  TABLE_OCCUPIED: { status: 409, title: 'The table already has an open order' },
  SPLIT_INVALID: { status: 422, title: 'The order cannot be split this way' },
  MERGE_INVALID: { status: 422, title: 'These orders cannot be merged' },
  MOVE_INVALID: { status: 422, title: 'The order cannot be moved this way' },
  TRANSFER_INVALID: { status: 422, title: 'These items cannot be transferred' },
  OUT_OF_STOCK: { status: 409, title: 'An item is out of stock' },
  OUTLET_CLOSED: { status: 409, title: 'The outlet is closed' },
  // Storefront (M3 task 6): the customer app at the table.
  QR_ORDERING_OFF: { status: 409, title: 'Ordering from the table is switched off here' },
  TABLE_SESSION_CLOSED: {
    status: 409,
    title: 'This table visit has ended; scan the table QR again',
  },
  // Online ordering (M5 task 6): takeaway and delivery from the customer app.
  ONLINE_ORDERING_OFF: {
    status: 409,
    title: 'Takeaway and delivery orders are not available at this outlet',
  },
  DELIVERY_BELOW_MINIMUM: {
    status: 422,
    title: 'The order is below the minimum for that delivery area',
  },
  ALREADY_RATED: { status: 409, title: 'This order has already been rated' },
  PRICE_CHANGED: { status: 409, title: 'Prices have changed' },
  // Customers, khata and rewards (M5 task 4).
  CUSTOMER_PHONE_TAKEN: { status: 409, title: 'Another customer has this phone number' },
  CUSTOMER_ERASED: { status: 409, title: 'This customer was erased' },
  ORDER_CUSTOMER_REQUIRED: { status: 409, title: 'Attach the customer to the order first' },
  ORDER_CUSTOMER_LOCKED: {
    status: 409,
    title: 'The order has khata or reward payments; void them to change the customer',
  },
  KHATA_ACCOUNT_REQUIRED: { status: 409, title: 'The customer has no khata account here' },
  KHATA_ACCOUNT_INACTIVE: { status: 409, title: 'The khata account is not active' },
  KHATA_PAYMENT_EXCEEDS_DUE: { status: 409, title: 'The payment is more than the customer owes' },
  KHATA_BALANCE_OUTSTANDING: { status: 409, title: 'The customer still owes on khata' },
  REWARDS_NOT_AVAILABLE: { status: 409, title: 'The customer cannot redeem rewards on this bill' },
  REWARD_REDEMPTION_EXCEEDS_LIMIT: {
    status: 409,
    title: 'That is more than can be redeemed on this bill',
  },
  REWARD_BALANCE_INSUFFICIENT: {
    status: 409,
    title: 'The customer does not have that much reward',
  },
  REWARD_SLAB_OVERLAP: { status: 409, title: 'That bill range overlaps another slab' },
  // Inventory (M5 task 2).
  STOCK_INSUFFICIENT: { status: 409, title: 'There is not enough stock for that' },
  STOCK_IN_DUPLICATE_INVOICE: {
    status: 409,
    title: 'Stock from this vendor was already recorded against that invoice number',
  },
  STOCK_UNIT_UNKNOWN: { status: 422, title: 'That unit does not apply to this stock item' },
  STOCK_ITEM_UNIT_LOCKED: {
    status: 409,
    title: 'The base unit cannot change once the item has stock movements',
  },
  RECIPE_TARGET_UNKNOWN: { status: 422, title: 'No such menu item, variant or add-on' },
  WASTAGE_NOT_PENDING: { status: 409, title: 'This wastage is not waiting for a decision' },
  STOCK_COUNT_STATE: { status: 409, title: 'The stock count cannot do this in its current state' },
  // Liquor and bar (M5 task 3).
  LIQUOR_ITEM_INVALID: {
    status: 422,
    title: 'A liquor profile needs a liquor stock item counted in ml',
  },
  PERMIT_NOT_AVAILABLE: { status: 409, title: 'That permit number is not available' },
  NO_PERMIT_AVAILABLE: { status: 409, title: 'There are no permit numbers left to give' },
  ORDER_NOT_LIQUOR: { status: 422, title: 'This order has no liquor on it' },
  ORDER_NOT_SETTLED: { status: 409, title: 'The order is not settled yet' },
  // Notifications (M5 task 7).
  NOTIFICATION_TEMPLATE_INVALID: { status: 422, title: 'That message text cannot be used' },
  NOTIFICATION_RECIPIENT_INVALID: {
    status: 422,
    title: 'That is not a valid phone number or email',
  },
  NOTIFICATION_NOT_RETRYABLE: { status: 409, title: 'This message cannot be retried' },
  NOTIFICATION_CHANNEL_UNAVAILABLE: {
    status: 409,
    title: 'The outlet has no provider to send on that channel',
  },
  // Menu (M2 task 5): publishing a draft with errors (`fields` names each problem by
  // target), and pricing or ordering before the outlet has published a menu.
  MENU_INVALID: { status: 422, title: 'The menu has errors; fix them before publishing' },
  MENU_NOT_PUBLISHED: { status: 409, title: 'This outlet has not published a menu yet' },
  // Integrations (M1 task 8). A partner webhook whose signature does not verify (the
  // delivery is kept in webhook_inbox and an alert event is emitted), and a partner
  // service that failed while the request waited for it (timeout, outage, refusal).
  WEBHOOK_SIGNATURE_INVALID: { status: 401, title: 'The webhook signature is not valid' },
  // Aggregators (M5 task 1): the outlet has no usable store on that platform, or the payout
  // file cannot be read.
  AGGREGATOR_STORE_UNAVAILABLE: {
    status: 409,
    title: 'This outlet has no connected, active store on that platform',
  },
  SETTLEMENT_FILE_INVALID: { status: 422, title: 'The payout file cannot be read' },
  // Delivery (M5 task 5): an address no zone serves or that cannot be placed on the map, a
  // delivery asked for without the address, a second live delivery, a lapsed or unusable quote,
  // a status that does not allow the action, and the pickup / drop codes.
  DELIVERY_NOT_SERVICEABLE: { status: 422, title: 'We cannot deliver to that address' },
  DELIVERY_REQUEST_REQUIRED: {
    status: 409,
    title: 'Add the delivery address and contact to the order first',
  },
  DELIVERY_ALREADY_LIVE: { status: 409, title: 'The order already has a delivery in progress' },
  DELIVERY_STATUS_INVALID: {
    status: 409,
    title: 'The delivery cannot do this in its current status',
  },
  DELIVERY_CHARGE_MISSING: {
    status: 409,
    title: 'Add a delivery charge in the outlet charges before taking delivery orders',
  },
  QUOTE_UNUSABLE: { status: 409, title: 'That quote cannot be booked; ask for new quotes' },
  OTP_INVALID: { status: 422, title: 'The delivery partner issued no such code' },
  CONNECTOR_FAILED: { status: 502, title: 'The partner service did not complete the request' },
  // Gateway (M1 task 9): a legacy (Laravel) route called for an outlet whose module
  // has moved to v3 (one writer per table, CLAUDE.md rule 10), and an upstream
  // (Laravel or the API) that failed or did not answer in time.
  OUTLET_MOVED: { status: 409, title: 'This outlet has moved to the new QRkey app' },
  BAD_GATEWAY: { status: 502, title: 'The upstream service failed' },
  GATEWAY_TIMEOUT: { status: 504, title: 'The upstream service did not answer in time' },
  INTERNAL: { status: 500, title: 'Internal error' },
  // HTTP-level failures raised by the framework rather than a module.
  BAD_REQUEST: { status: 400, title: 'The request could not be understood' },
  METHOD_NOT_ALLOWED: { status: 405, title: 'Method not allowed' },
  NOT_ACCEPTABLE: { status: 406, title: 'Not acceptable' },
  REQUEST_TIMEOUT: { status: 408, title: 'Request timeout' },
  CONFLICT: { status: 409, title: 'Conflict' },
  PAYLOAD_TOO_LARGE: { status: 413, title: 'The request body is too large' },
  UNSUPPORTED_MEDIA_TYPE: { status: 415, title: 'Unsupported media type' },
  SERVICE_UNAVAILABLE: { status: 503, title: 'The service is not ready' },
} as const satisfies Record<string, ErrorCodeSpec>;

export type ErrorCode = keyof typeof ERROR_CODES;

export const ERROR_CODE_LIST = Object.keys(ERROR_CODES) as ErrorCode[];

export const errorCodeSchema = z.enum(ERROR_CODE_LIST as [ErrorCode, ...ErrorCode[]]);

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && Object.hasOwn(ERROR_CODES, value);
}

export function httpStatusFor(code: ErrorCode): number {
  return ERROR_CODES[code].status;
}

/**
 * Problem `type` URI. RFC 9457 wants a URI; we use a URN so it does not
 * depend on a docs host that does not exist yet.
 */
export function problemTypeFor(code: string): string {
  return `urn:qrkey:problem:${code.toLowerCase().replaceAll('_', '-')}`;
}

/** Field-level validation messages, keyed by dotted path (`lines.0.qty`). `''` = the whole body. */
export type FieldErrors = Record<string, string[]>;

export const fieldErrorsSchema = z.record(z.string(), z.array(z.string()));

/**
 * RFC 9457 problem JSON as 05-api.md defines it:
 * `{ type, title, status, code, detail, fields?, requiredPermission? }`.
 * `detail` is always present (it defaults to the code's title).
 *
 * `code` and `requiredPermission` are validated by shape, not against today's
 * catalogues, so an older app can still read a problem carrying a code or a
 * permission added later (additive changes within /v1).
 *
 * Extension members (RFC 9457 section 3.2):
 * - `current` / `currentVersion`: on `VERSION_CONFLICT` (409), the resource as
 *   the server now has it, in its JSON wire form, and its `version`
 *   (05-api.md, Concurrency: "conflicts return 409 with the current
 *   resource"). Offline sync uses these to resolve the conflict.
 * - The object is loose: unknown members survive parsing, so a client keeps
 *   extensions added after it was built.
 */
export const problemSchema = z
  .looseObject({
    type: z.string().min(1),
    title: z.string().min(1),
    status: z.int().min(400).max(599),
    code: z.string().regex(/^[A-Z][A-Z0-9_]*$/),
    detail: z.string().min(1),
    fields: fieldErrorsSchema.optional(),
    requiredPermission: z.string().regex(PERMISSION_KEY_PATTERN).optional(),
    instance: z.string().optional(),
    /**
     * `OVERRIDE_REQUIRED`: the hash of the refused request (method, route, params,
     * query, body). Send it with the manager's PIN to `POST /v1/auth/override`,
     * then repeat the identical request with `X-Override-Token`.
     */
    actionHash: z.string().optional(),
    /** `ACCOUNT_LOCKED` / `RATE_LIMITED`: seconds until the next attempt may succeed. */
    retryAfterSeconds: z.int().min(0).optional(),
    /** `COMPANY_SELECTION_REQUIRED`: the companies the user may sign in to. */
    companies: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
    current: z.unknown().optional(),
    currentVersion: z.int().min(0).optional(),
  })
  .refine((p) => p.code !== 'OVERRIDE_REQUIRED' || p.requiredPermission !== undefined, {
    message: 'OVERRIDE_REQUIRED problems must name requiredPermission',
    path: ['requiredPermission'],
  });

export type Problem = z.infer<typeof problemSchema>;

export interface DomainErrorOptions {
  detail?: string;
  fields?: FieldErrors;
  requiredPermission?: PermissionKey;
  /**
   * `VERSION_CONFLICT`: the current resource in its JSON wire form (already
   * encoded with its response schema, e.g. `z.encode(orderSchema, order)`,
   * so it holds no bigints).
   */
  current?: unknown;
  /** `VERSION_CONFLICT`: the resource's current `version`. */
  currentVersion?: number;
  /** `OVERRIDE_REQUIRED`: hash of the refused request, to bind an override token to. */
  actionHash?: string;
  /** `ACCOUNT_LOCKED` / `RATE_LIMITED`: seconds until a retry may succeed. */
  retryAfterSeconds?: number;
  /** `COMPANY_SELECTION_REQUIRED`: the companies to choose from. */
  companies?: readonly { id: string; name: string }[];
  cause?: unknown;
}

/**
 * A typed, expected failure. No framework dependency: the HTTP layer maps it.
 * `detail` is shown to the user, so it must not carry secrets or internals.
 */
export class DomainError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly detail: string | undefined;
  readonly fields: FieldErrors | undefined;
  readonly requiredPermission: PermissionKey | undefined;
  readonly current: unknown;
  readonly currentVersion: number | undefined;
  readonly actionHash: string | undefined;
  readonly retryAfterSeconds: number | undefined;
  readonly companies: readonly { id: string; name: string }[] | undefined;

  constructor(
    code: 'OVERRIDE_REQUIRED',
    options: DomainErrorOptions & { requiredPermission: PermissionKey },
  );
  constructor(code: Exclude<ErrorCode, 'OVERRIDE_REQUIRED'>, options?: DomainErrorOptions);
  constructor(code: ErrorCode, options: DomainErrorOptions = {}) {
    if (code === 'OVERRIDE_REQUIRED' && options.requiredPermission === undefined) {
      throw new TypeError('OVERRIDE_REQUIRED needs requiredPermission');
    }
    super(
      options.detail ?? ERROR_CODES[code].title,
      options.cause === undefined ? undefined : { cause: options.cause },
    );
    this.name = 'DomainError';
    this.code = code;
    this.status = ERROR_CODES[code].status;
    this.detail = options.detail;
    this.fields = options.fields;
    this.requiredPermission = options.requiredPermission;
    this.current = options.current;
    this.currentVersion = options.currentVersion;
    this.actionHash = options.actionHash;
    this.retryAfterSeconds = options.retryAfterSeconds;
    this.companies = options.companies;
  }
}

export function isDomainError(err: unknown): err is DomainError {
  return err instanceof DomainError;
}

/**
 * Turn a `ZodError` into `VALIDATION_FAILED` with per-field messages.
 * `fields` has no prototype: paths are client-chosen (record keys), and a key
 * like `toString` or `constructor` must not hit an inherited member.
 */
export function validationError(error: z.ZodError, detail?: string): DomainError {
  const fields = Object.create(null) as FieldErrors;
  for (const issue of error.issues) {
    const key = issue.path.map(String).join('.');
    const messages = Object.hasOwn(fields, key) ? fields[key] : undefined;
    if (messages) messages.push(issue.message);
    else fields[key] = [issue.message];
  }
  return new DomainError('VALIDATION_FAILED', {
    detail: detail ?? 'One or more fields are invalid',
    fields,
  });
}

/**
 * Map any thrown value to a problem. Anything that is not a `DomainError`
 * becomes a generic `INTERNAL` problem; its message is never exposed.
 */
export function toProblem(err: unknown, instance?: string): Problem {
  const e = isDomainError(err) ? err : new DomainError('INTERNAL');
  const problem: Problem = {
    type: problemTypeFor(e.code),
    title: ERROR_CODES[e.code].title,
    status: e.status,
    code: e.code,
    detail: e.detail ?? ERROR_CODES[e.code].title,
  };
  if (e.fields !== undefined) problem.fields = e.fields;
  if (e.requiredPermission !== undefined) problem.requiredPermission = e.requiredPermission;
  if (instance !== undefined) problem.instance = instance;
  if (e.current !== undefined) problem.current = e.current;
  if (e.currentVersion !== undefined) problem.currentVersion = e.currentVersion;
  if (e.actionHash !== undefined) problem.actionHash = e.actionHash;
  if (e.retryAfterSeconds !== undefined) problem.retryAfterSeconds = e.retryAfterSeconds;
  if (e.companies !== undefined) problem.companies = e.companies.map((c) => ({ ...c }));
  return problem;
}
